// ============================================================================
// MANPOWER ALLOCATION PAGE - WITH PROJECT CODE & SITE DASHBOARDS
// Path: frontend/src/components/dashboard/pages/ManpowerAllocationPage.tsx
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  manpowerService,
  ManpowerProject,
  ExcelImportRow,
  ImportResult,
  EmployeeMasterData
} from '../../../services/manpower.service';
import ManpowerAttendanceView from './components/ManpowerAttendanceView';
import ManpowerSettingsModal from './components/ManpowerSettingsModal';
import ManpowerProjectCodeDashboard from './components/ManpowerProjectCodeDashboard';
import ManpowerSiteDashboard from './components/ManpowerSiteDashboard';
import ManpowerTransferPage from './components/ManpowerTransferPage';
import styles from './ManpowerAllocationPage.module.css';

interface ManpowerAllocationPageProps {
  onBack: () => void;
  onNavigate?: (page: string, params?: any) => void;
  user?: any;
  initialManagerId?: string;
}

const ManpowerAllocationPage: React.FC<ManpowerAllocationPageProps> = ({
  onBack,
  onNavigate,
  user,
  initialManagerId
}) => {
  // State
  const [projects, setProjects] = useState<ManpowerProject[]>([]);
  const [selectedProject, setSelectedProject] = useState<ManpowerProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // MasterSheet employees
  const [masterSheetEmployees, setMasterSheetEmployees] = useState<EmployeeMasterData[]>([]);
  
  // Modal states
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportResult, setShowImportResult] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  
  // Global search
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState<{
    project: ManpowerProject;
    employee: any;
  }[]>([]);
  
  // Form states
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectCode, setNewProjectCode] = useState('');
  const [newProjectLocation, setNewProjectLocation] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSite, setFilterSite] = useState('All');
  
  // View management
  const [currentView, setCurrentView] = useState<'dashboard' | 'allocation' | 'attendance' | 'transfer'>('dashboard');
  const [projectCodeFilter, setProjectCodeFilter] = useState<string | null>(null);
  const [dashboardViewType, setDashboardViewType] = useState<'projectCode' | 'site'>('projectCode');
  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  
  // Project editing
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectName, setEditingProjectName] = useState('');
  const [deleteConfirmProject, setDeleteConfirmProject] = useState<string | null>(null);
  
  // NEW: Checkbox selection state
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());

  // Load MasterSheet employees
  const loadMasterSheetEmployees = useCallback(async () => {
    try {
      console.log('📥 Loading ALL employees from MasterSheet...');
      const allEmployees = await manpowerService.getAllEmployeesFromMasterSheet();
      setMasterSheetEmployees(allEmployees);
      console.log(`✅ Loaded ${allEmployees.length} employees from MasterSheet`);
    } catch (err) {
      console.error('Error loading MasterSheet employees:', err);
      setError('Failed to load employee data from MasterSheet');
    }
  }, []);

  // Load projects
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedProjects = await manpowerService.getAllProjects();
      setProjects(fetchedProjects);
      
      if (fetchedProjects.length > 0 && !selectedProject) {
        setSelectedProject(fetchedProjects[0]);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Failed to load projects');
      setLoading(false);
    }
  }, [selectedProject]);

  // Load both projects AND MasterSheet on mount
  useEffect(() => {
    const loadAllData = async () => {
      await Promise.all([
        loadProjects(),
        loadMasterSheetEmployees()
      ]);
    };
    loadAllData();
  }, [loadProjects, loadMasterSheetEmployees]);

  // Create complete employee list from MasterSheet + assignments
  const allEmployees = useMemo(() => {
    const assignedMap = new Map<string, any>();
    
    projects.forEach(project => {
      project.employees.forEach(emp => {
        assignedMap.set(emp.empNumber, {
          ...emp,
          projectName: project.projectName,
          projectCode: emp.projectCode || project.projectCode || ''
        });
      });
    });

    const allEmployeesList = masterSheetEmployees.map(masterEmp => {
      const assigned = assignedMap.get(masterEmp.employeeNumber);
      
      if (assigned) {
        return assigned;
      } else {
        return {
          sNo: 0,
          name: masterEmp.name,
          empCode: masterEmp.employeeNumber.replace(/^EMP/i, ''),
          empNumber: masterEmp.employeeNumber,
          site: 'Unassigned',
          position: masterEmp.designation || 'Not Assigned',
          projectCode: '',
          projectName: 'Unassigned',
          designation: masterEmp.designation,
          department: masterEmp.department,
          nationality: masterEmp.nationality,
          joiningDate: masterEmp.joiningDate
        };
      }
    });

    return allEmployeesList;
  }, [projects, masterSheetEmployees]);

  // Handle project code filter from dashboard
  const handleProjectCodeFilter = useCallback((projectCode: string | null) => {
    setProjectCodeFilter(projectCode);
    
    if (projectCode) {
      const projectWithCode = projects.find(p => 
        p.employees.some(emp => emp.projectCode === projectCode)
      );
      
      if (projectWithCode) {
        setSelectedProject(projectWithCode);
        setCurrentView('allocation');
      }
    }
  }, [projects]);

  // Handle site filter from site dashboard
  const handleSiteFilter = useCallback((site: string | null) => {
    setSiteFilter(site);
    
    if (site) {
      const projectWithSite = projects.find(p => 
        p.employees.some(emp => emp.site === site)
      );
      
      if (projectWithSite) {
        setSelectedProject(projectWithSite);
        setFilterSite(site);
        setCurrentView('allocation');
      }
    }
  }, [projects]);

  // NEW: Handle checkbox selection
  const handleProjectCheckboxChange = (projectId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newSelected = new Set(selectedProjectIds);
    if (e.target.checked) {
      newSelected.add(projectId);
    } else {
      newSelected.delete(projectId);
    }
    setSelectedProjectIds(newSelected);
  };

  // Global search
  const handleGlobalSearch = useCallback((query: string) => {
    setGlobalSearch(query);
    
    if (!query.trim()) {
      setGlobalSearchResults([]);
      return;
    }
    
    const results: Array<{project: ManpowerProject; employee: any}> = [];
    const searchLower = query.toLowerCase();
    
    projects.forEach(project => {
      project.employees.forEach(emp => {
        if (
          emp.name.toLowerCase().includes(searchLower) ||
          emp.empCode.toLowerCase().includes(searchLower) ||
          emp.empNumber.toLowerCase().includes(searchLower) ||
          (emp.projectCode && emp.projectCode.toLowerCase().includes(searchLower))
        ) {
          results.push({
            project: project,
            employee: emp
          });
        }
      });
    });
    
    setGlobalSearchResults(results);
  }, [projects]);
  
  const handleSelectGlobalSearchResult = (projectName: string) => {
    const project = projects.find(p => p.projectName === projectName);
    if (project) {
      setSelectedProject(project);
      setGlobalSearch('');
      setGlobalSearchResults([]);
      setCurrentView('allocation');
    }
  };

  // Download template
  const handleDownloadTemplate = () => {
    const template = manpowerService.generateExcelTemplate();
    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, 'Manpower_Import_Template.xlsx');
    setSuccess('Template downloaded successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Create project
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      setError('Project name is required');
      return;
    }

    try {
      await manpowerService.createProject(
        newProjectName,
        {
          projectCode: newProjectCode,
          location: newProjectLocation,
          createdBy: user?.uid || initialManagerId || 'system'
        }
      );
      
      setSuccess('Project created successfully');
      setShowCreateProject(false);
      setNewProjectName('');
      setNewProjectCode('');
      setNewProjectLocation('');
      await loadProjects();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to create project');
    }
  };

  // Import Excel
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !selectedProject) {
      setError('Please select a file and project');
      return;
    }

    setImporting(true);
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData: ExcelImportRow[] = XLSX.utils.sheet_to_json(worksheet);

      const result = await manpowerService.importEmployeesFromExcel(
        selectedProject.projectName,
        jsonData,
        user?.uid || initialManagerId || 'system',
        replaceExisting
      );

      setImportResult(result);
      setShowImportModal(false);
      setShowImportResult(true);
      setSelectedFile(null);
      setReplaceExisting(false);
      await loadProjects();
      await loadMasterSheetEmployees();
    } catch (err) {
      setError('Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  // Export Excel
  const handleExport = () => {
    if (!selectedProject) return;

    const exportData = selectedProject.employees.map((emp, index) => ({
      'S.NO': index + 1,
      'NAME': emp.name,
      'EMP. CODE': emp.empCode,
      'PROJECT CODE': emp.projectCode || '',
      'SITE': emp.site,
      'POSITION': emp.position,
      'DESIGNATION': emp.designation || '',
      'DEPARTMENT': emp.department || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
    XLSX.writeFile(workbook, `${selectedProject.projectName}_Employees.xlsx`);
    setSuccess('Data exported successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Edit project name
  const handleStartEditProject = (project: ManpowerProject, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProjectId(project.id);
    setEditingProjectName(project.projectName);
  };

  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setEditingProjectName('');
  };

  const handleSaveProjectName = async (projectId: string, oldName: string) => {
    if (!editingProjectName.trim() || editingProjectName === oldName) {
      setEditingProjectId(null);
      return;
    }

    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      await manpowerService.createProject(
        editingProjectName,
        {
          projectCode: project.projectCode,
          location: project.location,
          createdBy: user?.uid || initialManagerId || 'system'
        }
      );
      
      if (project.employees.length > 0) {
        const excelData: any[] = project.employees.map((emp, index) => ({
          'S.NO': index + 1,
          'NAME': emp.name,
          'EMP. CODE': emp.empCode,
          'PROJECT CODE': emp.projectCode || '',
          'SITE': emp.site,
          'POSITION': emp.position,
          'DESIGNATION': emp.designation || '',
          'DEPARTMENT': emp.department || ''
        }));
        
        await manpowerService.importEmployeesFromExcel(
          editingProjectName,
          excelData,
          user?.uid || initialManagerId || 'system',
          false
        );
      }
      
      await manpowerService.deleteProject(oldName);

      setSuccess(`Project renamed to "${editingProjectName}"`);
      setEditingProjectId(null);
      setEditingProjectName('');
      
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
      
      await loadProjects();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to rename project');
      setEditingProjectId(null);
    }
  };

  // Delete project
  const handleRequestDelete = (projectName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmProject(projectName);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmProject(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmProject) return;
    
    try {
      await manpowerService.deleteProject(deleteConfirmProject);
      setSuccess(`Project "${deleteConfirmProject}" deleted successfully`);
      setDeleteConfirmProject(null);
      
      if (selectedProject?.projectName === deleteConfirmProject) {
        setSelectedProject(null);
      }
      
      await loadProjects();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete project');
      setDeleteConfirmProject(null);
    }
  };

  // Remove employee
  const handleRemoveEmployee = async (empCode: string) => {
    if (!selectedProject) return;

    try {
      await manpowerService.removeEmployeeFromProject(
        selectedProject.projectName,
        empCode,
        user?.uid || initialManagerId || 'system'
      );
      
      setSuccess('Employee removed successfully');
      await loadProjects();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to remove employee');
    }
  };

  // Filtered employees
  const filteredEmployees = useMemo(() => {
    if (!selectedProject) return [];
    
    let filtered = selectedProject.employees;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(query) ||
        emp.empCode.toLowerCase().includes(query) ||
        emp.empNumber.toLowerCase().includes(query) ||
        emp.site.toLowerCase().includes(query) ||
        emp.position.toLowerCase().includes(query)
      );
    }
    
    if (filterSite !== 'All') {
      filtered = filtered.filter(emp => emp.site === filterSite);
    }
    
    return filtered;
  }, [selectedProject, searchQuery, filterSite]);

  const uniqueSites = useMemo(() => {
    if (!selectedProject?.employees) return [];
    const sites = new Set(selectedProject.employees.map(emp => emp.site));
    return Array.from(sites).sort();
  }, [selectedProject]);

  // Handle transfer completion
  const handleTransferComplete = async () => {
    await loadProjects();
    await loadMasterSheetEmployees();
    setSuccess('Employees transferred successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  // ========================================================================
  // RENDER TRANSFER PAGE VIEW
  // ========================================================================
  if (currentView === 'transfer') {
    return (
      <ManpowerTransferPage
        onBack={() => setCurrentView('dashboard')}
        onTransferComplete={handleTransferComplete}
        currentUser={{
          uid: user?.uid || initialManagerId || 'system',
          name: user?.name || user?.displayName,
          displayName: user?.displayName
        }}
      />
    );
  }

  // ========================================================================
  // RENDER MAIN PAGE
  // ========================================================================
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
          <h1 className={styles.title}>Manpower Allocation</h1>
        </div>
        <div className={styles.headerRight}>
          <button 
            onClick={() => onNavigate?.('manpower-masons')}
            className={styles.primaryButton}
          >
            Mason Dashboard
          </button>
          <button 
            onClick={() => setCurrentView('transfer')}
            className={styles.transferButton}
          >
            Transfer Employees
          </button>
          <button 
            onClick={handleDownloadTemplate}
            className={styles.actionButton}
          >
            Download Template
          </button>
          <button 
            onClick={() => setShowSettingsModal(true)}
            className={styles.actionButton}
          >
            Settings
          </button>
          <button 
            onClick={() => setShowCreateProject(true)}
            className={styles.actionButton}
          >
            New Project
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className={styles.errorAlert}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className={styles.successAlert}>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Global Search */}
      <div className={styles.globalSearch}>
        <input
          type="text"
          placeholder="Search employee by name, code, or PIN across all projects..."
          value={globalSearch}
          onChange={(e) => handleGlobalSearch(e.target.value)}
          className={styles.globalSearchInput}
        />
        {globalSearchResults.length > 0 && (
          <div className={styles.globalSearchResults}>
            {globalSearchResults.map((result, index) => (
              <div
                key={index}
                className={styles.searchResultItem}
                onClick={() => handleSelectGlobalSearchResult(result.project.projectName)}
              >
                <div className={styles.searchResultName}>{result.employee.name}</div>
                <div className={styles.searchResultDetails}>
                  <span>Code: {result.employee.empCode}</span>
                  <span>PIN: {result.employee.empNumber.replace(/^EMP/i, '')}</span>
                  {result.employee.projectCode && <span>Project: {result.employee.projectCode}</span>}
                  <span>In: {result.project.projectName}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {globalSearch && globalSearchResults.length === 0 && (
          <div className={styles.noResults}>No employees found matching "{globalSearch}"</div>
        )}
      </div>

      {/* Dashboard or Regular View */}
      {currentView === 'dashboard' ? (
        <div className={styles.dashboardContainer}>
          <div className={styles.dashboardHeader}>
            <h2>Manpower Dashboard</h2>
            <div className={styles.dashboardActions}>
              {/* Toggle between Project Code and Site */}
              <div className={styles.dashboardToggle}>
                <button
                  className={`${styles.toggleButton} ${dashboardViewType === 'projectCode' ? styles.activeToggle : ''}`}
                  onClick={() => setDashboardViewType('projectCode')}
                >
                  Project Code View
                </button>
                <button
                  className={`${styles.toggleButton} ${dashboardViewType === 'site' ? styles.activeToggle : ''}`}
                  onClick={() => setDashboardViewType('site')}
                >
                  Site Location View
                </button>
              </div>
              
              <button 
                onClick={() => setCurrentView('allocation')}
                className={styles.viewProjectsButton}
              >
                View Projects
              </button>
            </div>
          </div>
          
          {dashboardViewType === 'projectCode' ? (
            <ManpowerProjectCodeDashboard
              employees={allEmployees}
              projectName="All Projects"
              onFilterChange={handleProjectCodeFilter}
            />
          ) : (
            <ManpowerSiteDashboard
              employees={allEmployees}
              projectName="All Projects"
              onFilterChange={handleSiteFilter}
            />
          )}
        </div>
      ) : (
        <div className={styles.mainContent}>
          {/* Sidebar with Checkbox Selection */}
          <div className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h3>Projects ({projects.length})</h3>
              <button 
                onClick={() => setCurrentView('dashboard')}
                className={styles.dashboardViewButton}
                title="View Dashboard"
              >
                Dashboard
              </button>
            </div>
            <div className={styles.projectList}>
              {loading ? (
                <div className={styles.loading}>Loading...</div>
              ) : projects.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No projects yet</p>
                </div>
              ) : (
                projects.map(project => {
                  const isSelected = selectedProjectIds.has(project.id);
                  
                  return (
                    <div
                      key={project.id}
                      className={`${styles.projectCard} ${
                        selectedProject?.id === project.id ? styles.active : ''
                      } ${isSelected ? styles.selected : ''}`}
                      onClick={() => {
                        if (editingProjectId !== project.id && deleteConfirmProject !== project.projectName) {
                          setSelectedProject(project);
                        }
                      }}
                    >
                      {editingProjectId === project.id ? (
                        <div className={styles.editMode} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editingProjectName}
                            onChange={(e) => setEditingProjectName(e.target.value)}
                            className={styles.projectNameInput}
                            placeholder="Enter new project name..."
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveProjectName(project.id, project.projectName);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                          />
                          <div className={styles.editButtons}>
                            <button 
                              onClick={() => handleSaveProjectName(project.id, project.projectName)}
                              className={styles.saveEditBtn}
                              title="Save"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              Save
                            </button>
                            <button 
                              onClick={handleCancelEdit}
                              className={styles.cancelEditBtn}
                              title="Cancel"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : deleteConfirmProject === project.projectName ? (
                        <div className={styles.deleteMode} onClick={(e) => e.stopPropagation()}>
                          <div className={styles.deleteWarning}>
                            <svg className={styles.deleteIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                              <line x1="12" y1="9" x2="12" y2="13"></line>
                              <line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                            <div className={styles.deleteText}>
                              <p className={styles.deleteTitle}>Delete this project?</p>
                              <p className={styles.deleteSubtext}>
                                {project.totalEmployees} employee{project.totalEmployees !== 1 ? 's' : ''} will be unassigned
                              </p>
                            </div>
                          </div>
                          <div className={styles.deleteButtons}>
                            <button 
                              onClick={handleConfirmDelete}
                              className={styles.confirmDeleteBtn}
                            >
                              Yes, Delete
                            </button>
                            <button 
                              onClick={handleCancelDelete}
                              className={styles.cancelDeleteBtn}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            className={styles.projectCheckbox}
                            checked={isSelected}
                            onChange={(e) => handleProjectCheckboxChange(project.id, e)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          
                          <div className={styles.projectCardContent}>
                            <div className={styles.projectCardHeader}>
                              <h4 className={styles.projectName} title={project.projectName}>
                                {project.projectName}
                              </h4>
                              
                              {isSelected && (
                                <div className={styles.projectActions}>
                                  <button
                                    onClick={(e) => handleStartEditProject(project, e)}
                                    className={styles.editProjectBtn}
                                    title="Edit project name"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => handleRequestDelete(project.projectName, e)}
                                    className={styles.deleteProjectBtn}
                                    title="Delete project"
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            <div className={styles.projectCardInfo}>
                              <div className={styles.employeeCount}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="9" cy="7" r="4"></circle>
                                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                                {project.totalEmployees} employee{project.totalEmployees !== 1 ? 's' : ''}
                              </div>
                              {project.location && (
                                <div className={styles.projectLocation} title={project.location}>
                                  <svg className={styles.locationIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                    <circle cx="12" cy="10" r="3"></circle>
                                  </svg>
                                  {project.location}
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className={styles.content}>
            {selectedProject ? (
              <>
                <div className={styles.contentHeader}>
                  <div>
                    <h2>{selectedProject.projectName}</h2>
                    <p className={styles.projectMeta}>
                      {selectedProject.totalEmployees} employees
                      {selectedProject.location && ` • ${selectedProject.location}`}
                    </p>
                  </div>
                  <div className={styles.contentActions}>
                    <button 
                      onClick={() => setShowImportModal(true)}
                      className={styles.actionButton}
                    >
                      Import Excel
                    </button>
                    <button 
                      onClick={handleExport}
                      className={styles.actionButton}
                      disabled={selectedProject.employees.length === 0}
                    >
                      Export Excel
                    </button>
                  </div>
                </div>

                {/* View Toggle */}
                <div className={styles.viewToggle}>
                  <button
                    className={currentView === 'allocation' ? styles.activeViewButton : styles.viewButton}
                    onClick={() => setCurrentView('allocation')}
                  >
                    Employee Allocation
                  </button>
                  <button
                    className={currentView === 'attendance' ? styles.activeViewButton : styles.viewButton}
                    onClick={() => setCurrentView('attendance')}
                  >
                    Attendance Report
                  </button>
                </div>

                {currentView === 'allocation' ? (
                  <>
                    <div className={styles.filterBar}>
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={styles.searchInput}
                      />
                      <select
                        value={filterSite}
                        onChange={(e) => setFilterSite(e.target.value)}
                        className={styles.filterSelect}
                      >
                        <option value="All">All Sites</option>
                        {uniqueSites.map(site => (
                          <option key={site} value={site}>{site}</option>
                        ))}
                      </select>
                    </div>

                    <div className={styles.tableContainer}>
                      {filteredEmployees.length === 0 ? (
                        <div className={styles.emptyState}>
                          <h3>No Employees</h3>
                          <p>Import employees from Excel to get started</p>
                        </div>
                      ) : (
                        <table className={styles.table}>
                          <thead>
                            <tr>
                              <th>S.NO</th>
                              <th>Employee Name</th>
                              <th>Code</th>
                              <th>Project Code</th>
                              <th>Site</th>
                              <th>Position</th>
                              <th>Designation</th>
                              <th>Department</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredEmployees.map((employee, index) => (
                              <tr key={employee.empCode}>
                                <td>{index + 1}</td>
                                <td className={styles.employeeName}>{employee.name}</td>
                                <td>
                                  <code className={styles.empCode}>{employee.empCode}</code>
                                </td>
                                <td>{employee.projectCode || '-'}</td>
                                <td>{employee.site}</td>
                                <td>{employee.position}</td>
                                <td>{employee.designation || '-'}</td>
                                <td>{employee.department || '-'}</td>
                                <td>
                                  <button
                                    onClick={() => handleRemoveEmployee(employee.empCode)}
                                    className={styles.removeButton}
                                    title="Remove from project"
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                ) : (
                  <ManpowerAttendanceView
                    employees={selectedProject.employees || []}
                    projectName={selectedProject.projectName}
                  />
                )}
              </>
            ) : (
              <div className={styles.emptyState}>
                <h3>No Project Selected</h3>
                <p>Select a project from the sidebar or create a new one</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals - Create Project, Import, Import Result, Settings */}
      {showCreateProject && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Create New Project</h3>
              <button onClick={() => setShowCreateProject(false)} className={styles.closeButton}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Project Name *</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className={styles.input}
                  placeholder="Enter project name"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Project Code</label>
                <input
                  type="text"
                  value={newProjectCode}
                  onChange={(e) => setNewProjectCode(e.target.value)}
                  className={styles.input}
                  placeholder="Enter project code (optional)"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Location</label>
                <input
                  type="text"
                  value={newProjectLocation}
                  onChange={(e) => setNewProjectLocation(e.target.value)}
                  className={styles.input}
                  placeholder="Enter location (optional)"
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button onClick={() => setShowCreateProject(false)} className={styles.cancelButton}>
                Cancel
              </button>
              <button onClick={handleCreateProject} className={styles.submitButton}>
                Create Project
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Import Employees</h3>
              <button onClick={() => setShowImportModal(false)} className={styles.closeButton}>×</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Select Excel File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
                {selectedFile && (
                  <p className={styles.fileName}>Selected: {selectedFile.name}</p>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={replaceExisting}
                    onChange={(e) => setReplaceExisting(e.target.checked)}
                  />
                  Replace all existing employees
                </label>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button onClick={() => setShowImportModal(false)} className={styles.cancelButton}>
                Cancel
              </button>
              <button 
                onClick={handleImport} 
                className={styles.submitButton}
                disabled={!selectedFile || importing}
              >
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showImportResult && importResult && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Import Results</h3>
              <button onClick={() => setShowImportResult(false)} className={styles.closeButton}>×</button>
            </div>
            <div className={styles.modalBody}>
              {importResult.success ? (
                <div className={styles.successBox}>
                  <h4>Import Successful</h4>
                  <div className={styles.statsGrid}>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Total Rows</span>
                      <span className={styles.statValue}>{importResult.totalRows}</span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Successful</span>
                      <span className={`${styles.statValue} ${styles.success}`}>
                        {importResult.successfulImports}
                      </span>
                    </div>
                    <div className={styles.statItem}>
                      <span className={styles.statLabel}>Failed</span>
                      <span className={`${styles.statValue} ${styles.error}`}>
                        {importResult.failedImports}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.errorBox}>
                  <h4>Import Failed</h4>
                </div>
              )}

              {importResult.errors.length > 0 && (
                <div className={styles.errorList}>
                  <h5>Errors:</h5>
                  <ul>
                    {importResult.errors.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                onClick={() => setShowImportResult(false)}
                className={styles.submitButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ManpowerSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onEmployeeAssigned={async () => {
          await loadProjects();
          await loadMasterSheetEmployees();
        }}
      />
    </div>
  );
};

export default ManpowerAllocationPage;